import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, PenLine, FileText } from "lucide-react";
import { API, apiFetch } from "@/portal/api";
import { SignaturePad } from "@/portal/SignaturePad";

const LOGO_IMG = "/img/n148kmw9_image.webp";

const CooperationSignPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | signed
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const data = await apiFetch(`/public/cooperation/${token}`);
      setInfo(data);
      setStatus(data.status === "signed" ? "signed" : "valid");
    } catch (e) { setStatus("invalid"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const sign = async (signature) => {
    setSaving(true); setError(null);
    try {
      await apiFetch(`/public/cooperation/${token}/sign`, { method: "POST", body: { signature } });
      await load();
    } catch (e) { setError(e.message || "Podpis se nezdařil"); }
    finally { setSaving(false); }
  };

  const label = info?.coop_type === "influencer" ? "Smlouva o spolupráci (influencer / ambasador)" : "Smlouva o obchodní spolupráci";

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4" data-testid="coop-sign-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO_IMG} alt="EffectDeal" className="w-10 h-10 object-contain" />
          <span className="font-['Cabinet_Grotesk'] font-bold text-xl tracking-wide">EFFECTDEAL</span>
        </Link>

        {status === "checking" && (
          <div className="glass rounded-2xl p-10 text-center"><Loader2 className="w-8 h-8 text-orange-400 animate-spin mx-auto" /></div>
        )}

        {status === "invalid" && (
          <div className="glass rounded-2xl p-8 text-center" data-testid="coop-sign-invalid">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center"><AlertCircle className="w-7 h-7 text-red-400" /></div>
            <h1 className="font-['Cabinet_Grotesk'] text-xl font-bold mb-2">Neplatný odkaz</h1>
            <p className="text-zinc-400 text-sm">Tento odkaz k podpisu je neplatný nebo vypršel. Kontaktujte prosím EffectDeal.</p>
          </div>
        )}

        {(status === "valid" || status === "signed") && info && (
          <div className="space-y-5">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 text-orange-400 mb-1"><FileText className="w-5 h-5" /><span className="text-sm font-medium">{label}</span></div>
              <h1 className="font-['Cabinet_Grotesk'] text-2xl font-bold">Dobrý den, {info.partner_name}</h1>
              <p className="text-zinc-400 text-sm mt-2">Zkontrolujte prosím smlouvu níže a poté ji elektronicky podepište. Elektronický podpis je závazný.</p>
            </div>

            <div className="glass rounded-2xl p-2 overflow-hidden">
              <iframe title="Smlouva" src={`${API}/public/cooperation/${token}/pdf`} className="w-full rounded-xl bg-white" style={{ height: "70vh" }} data-testid="coop-sign-pdf" />
            </div>

            {status === "signed" ? (
              <div className="glass rounded-2xl p-8 text-center" data-testid="coop-sign-success">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center"><CheckCircle2 className="w-7 h-7 text-emerald-400" /></div>
                <h2 className="font-['Cabinet_Grotesk'] text-xl font-bold mb-1">Smlouva je podepsána</h2>
                <p className="text-zinc-400 text-sm">Děkujeme! Podepsanou smlouvu máme uloženou. V případě dotazů nás kontaktujte.</p>
              </div>
            ) : (
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4"><PenLine className="w-5 h-5 text-orange-400" /><h2 className="font-['Cabinet_Grotesk'] text-lg font-bold">Podpis partnera</h2></div>
                <SignaturePad onSave={sign} saving={saving} />
                {error && <p className="text-red-400 text-sm mt-3" data-testid="coop-sign-error">{error}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CooperationSignPage;
